import "react-toastify/dist/ReactToastify.css";

import { useEffect, useState } from "react";

import { Spin } from "antd";
import axios from "axios";
import { ethers } from "ethers";
import Parse from "parse";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";

import ClaimTopicsPage from "./components/ClaimTopicsPage.jsx";
import CreateClaimTopic from "./components/CreateClaimTopic.jsx";
import CreateDigitalId from "./components/CreateDigitalId.jsx";
import CreatePassword from "./components/CreatePasswordPage.jsx";
import CreateTrustedIssuer from "./components/CreateTrustedIssuer.jsx";
import CSPMeta from "./components/CSPMeta.jsx";
import DigitalIdentityDetailView from "./components/DigitalIdentityDetailPage.jsx";
import EditClaims from "./components/EditClaims.jsx";
import EditClaimsSummaryView from "./components/EditClaimsSummaryView.jsx";
import EditClaimTopic from "./components/EditClaimTopic.jsx";
import ForgotPassword from "./components/ForgotPasswordPage.jsx";
import Home from "./components/Home.jsx";
import IdentitiesPage from "./components/IdentitiesPage.jsx";
import Layout from "./components/Layout";
import Login from "./components/LoginPage.jsx";
import MintPage from "./components/MintPage.jsx";
import NavBar from "./components/NavBar.jsx";
import Protected from "./components/Protected";
import ResetPassword from "./components/ResetPasswordPage.jsx";
import TrustedIssuersPage from "./components/TrustedIssuersPage.jsx";
import ViewClaimTopic from "./components/ViewClaimTopic";
import { RoleContext } from "./context/RoleContext";
import BlockchainService from "./services/BlockchainService.js";
import { endSession } from "./services/endSession";
import parseInitialize from "./services/parseInitialize";
import { onSessionLost, recordSignOutReason } from "./services/tokenRenewal";
import AutoLogout from "./utils/AutoLogout";

const validateToken = async (token: string) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_PARSE_SERVER_URL}/legacy-auth/validate`, {
      headers: {
        "x-parse-session-token": token,
      },
    });

    const data = response.data;
    return {
      valid: data?.valid || false,
      roles: data?.user?.roles || [],
      user: data?.user,
      dfnsToken: data?.dfnsToken,
    };
  } catch (error) {
    console.error("Error validating token:", error);
    return {
      valid: false,
      roles: [],
      dfnsToken: null,
    };
  }
};

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [blockchainService, setBlockchainService] = useState<BlockchainService | null>(null);
  const [user, setUser] = useState(null); // State to hold user
  const [role, setRole] = useState<any>([]);
  const [dfnsToken, setDfnsToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true); // New state for initialization
  //let provider: ethers.providers.Provider | null = null;
  let [provider, setProvider] = useState<ethers.providers.Provider | null>(null);
  // Define the getToken function
  const getToken = async (request: any) => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_PARSE_SERVER_URL}/legacy-auth/login`, request);
      const data = response.data;
      return {
        token: data?.access_token || "",
        jwt: data?.jwt || "",
        refreshToken: data?.refreshToken || "",
        roles: data?.user?.roles || [],
        user: data?.user,
        dfnsToken: data?.dfns_token,
      };
    } catch (error) {
      console.log("Error during authentication:", error);
      return {
        token: "",
        jwt: "",
        roles: [],
        user: null,
        dfnsToken: null,
      };
    }
  };

  // Abstracted function to initialize BlockchainService
  const initializeBlockchainService = (provider: ethers.providers.Provider) => {
    console.log("Initializing BlockchainService...");
    const _blockchainService = new BlockchainService(
      provider,
      process.env.REACT_APP_HARDHAT_CONTRACT_ADDRESS || "",
      process.env.REACT_APP_HARDHAT_IDENTITY_FACTORY_ADDRESS || ""
    );
    setBlockchainService(_blockchainService);
  };

  // Define the onLogout function for email/password login
  const onLogoutEmailPassword = async (reason?: "idle" | "session") => {
    // First, before anything tears the session down. The cloud function authenticates
    // with this bearer and resolves the caller from a live Parse session, so both the
    // server logout below and Parse.User.logOut() have to come after it.
    const authJwt = localStorage.getItem("authJwt");
    if (authJwt) {
      try {
        await Parse.Cloud.run("revokeJWTToken", { token: authJwt });
      } catch (error) {
        // Best effort: a user who asked to leave must not be stranded by this failing.
        console.error("Could not revoke the JWT on logout:", error);
      }
    }

    await endSession();

    // Reset client-side state
    setRole([]);
    setDfnsToken(null);
    setUser(null);
    setIsConnected(false);
    setBlockchainService(null);

    // Left for the login page rather than toasted: a toast is gone in seconds, and the
    // person who was signed out for inactivity is by definition not at the screen.
    // A refused renewal knows a truer reason than inactivity.
    if (reason === "session") recordSignOutReason();
    else if (reason === "idle") recordSignOutReason("idle");
    else toast.success("Logged out successfully.");
  };

  // Define the onLogin function (Username/Password Login)
  const onLogin = async (email: string, password: string) => {
    const { token, jwt, refreshToken, roles, user, dfnsToken } = await getToken({ email, password });

    if (roles.length > 0) {
      // Store the session token AND the auth JWT BEFORE parseInitialize(), because
      // parseInitialize() reads `authJwt` from localStorage to install the
      // `Authorization: Bearer` header the backend `withAuth` gate checks. Storing it
      // after would leave the whoami call (and every gated cloud call) tokenless.
      localStorage.setItem("sessionToken", token);
      localStorage.setItem("authJwt", jwt);
      if (refreshToken) localStorage.setItem("refreshToken", refreshToken);

      // Ensure the Parse SDK has the session token + JWT header attached before setRole
      await parseInitialize();

      // Confirm the caller's roles via the backend `whoami` cloud function — the
      // AUTHORITATIVE set the server's `withAuth` gates read (re-derived from the JWT
      // over native _Role), not just the legacy-auth login payload's roles array.
      const ADMIN_UI_ROLES = ["CentralAuthority", "admin", "TrustedIssuer", "reviewer"];
      let resolvedRoles: string[] = roles;
      try {
        const who = await Parse.Cloud.run("whoami");
        resolvedRoles = who?.roles ?? [];
        console.log("whoami resolved roles:", resolvedRoles);
      } catch (err) {
        console.error("whoami failed; falling back to login roles:", err);
      }

      const hasAdminUiAccess = resolvedRoles.some((r) => ADMIN_UI_ROLES.includes(r));
      if (!hasAdminUiAccess) {
        toast.error("This account has no Admin UI access (needs CentralAuthority, admin, TrustedIssuer, or reviewer).");
        localStorage.removeItem("sessionToken");
        localStorage.removeItem("authJwt");
        localStorage.removeItem("refreshToken");
        return;
      }

      setRole([...resolvedRoles]);
      setUser(user);
      setDfnsToken(dfnsToken);
      localStorage.setItem("sessionToken", token);
      setIsConnected(true);
      //const provider = await setupProvider();
      if (!provider) {
        console.error("❌ Failed to initialize provider.");
        toast.error("Could not initialize provider. Please try again.");
        return;
      }

      console.log("✅ Provider initialized:", provider);
      initializeBlockchainService(provider);
    } else {
      toast.error("Incorrect username / password");
    }
  };

  useEffect(() => {
    setupProvider().then(setProvider);
  }, []);

  // The interceptor lives outside React, so it hands the teardown back here.
  useEffect(() => {
    onSessionLost(() => {
      void onLogoutEmailPassword("session");
    });
  });

  // DFNS is the only supported wallet, so this always resolves to the
  // dedicated RPC provider rather than any injected wallet (e.g. MetaMask).
  async function setupProvider(): Promise<ethers.providers.Provider | null> {
    let newProvider: ethers.providers.Provider | null = null;
    console.log("🔍 Setting up RPC provider...");

    const rpcUrl = process.env.REACT_APP_RPC_URL;
    const chainId = Number(process.env.REACT_APP_HARDHAT_CHAIN_ID);
    if (rpcUrl) {
      // Pin the network so ethers skips the eth_chainId detection call on every request.
      newProvider = new ethers.providers.StaticJsonRpcProvider(rpcUrl, chainId ? { chainId, name: "unknown" } : undefined);
      if (newProvider instanceof ethers.providers.StaticJsonRpcProvider) {
        newProvider.polling = false;
        newProvider._pollingInterval = Infinity;
      }
    }

    if (newProvider) {
      setProvider(newProvider);
      return newProvider;
    }

    console.error("❌ No provider available! Please set REACT_APP_RPC_URL.");
    return null;
  }

  const restoreSession = async () => {
    const token = localStorage.getItem("sessionToken");
    if (token) {
      const { valid, roles, user, dfnsToken } = await validateToken(token);
      if (valid && roles.length > 0) {
        setRole(roles);
        setUser(user);
        setDfnsToken(dfnsToken);
        setIsConnected(true);
      } else {
        // Token is invalid or roles are empty
        localStorage.removeItem("sessionToken");
      }
    }
    setInitializing(false);
  };

  useEffect(() => {
    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle loading state when roles change
  useEffect(() => {
    if (role.length > 0) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [role]);

  useEffect(() => {
    if (provider && isConnected) {
      initializeBlockchainService(provider);
    }
  }, [provider, isConnected]);

  if (initializing) {
    return (
      <div className="z-50 h-screen w-screen flex justify-center items-center">
        <Spin />
      </div>
    );
  }

  return (
    <>
      {/* <CSPMeta /> */}
      <RoleContext.Provider value={{ role, setRole, dfnsToken, setDfnsToken, user, setUser }}>
        {/* Loading Spinner Overlay */}
        {loading && (
          <div className="z-50 h-screen w-screen overflow-hidden absolute top-0 left-0 flex justify-center items-center bg-[#00000040]">
            <Spin />
          </div>
        )}
        <Router>
          <AutoLogout enabled={role.length > 0} onIdle={() => onLogoutEmailPassword("idle")} />
          {/* Navigation Bar (Only visible when logged in) */}
          {role.length > 0 && (
            <div className={`topnav p-0`}>
              <NavBar onLogout={onLogoutEmailPassword} role={role} />
            </div>
          )}

          <Layout>
            <div className={`${role.length === 0 ? "p-0 -ml-4 overflow-hidden" : "content"}`}>
              <ToastContainer
                position="top-right"
                className="toast-background"
                progressClassName="toast-progress-bar"
                autoClose={4000}
                closeOnClick
                pauseOnHover
              />
              {/* Application Routes */}
              <Routes>
                <Route
                  path="/"
                  element={
                    <Protected role={"CentralAuthority"} roles={role}>
                      <Home />
                    </Protected>
                  }
                />
                <Route path="/login" element={<Login onLogin={onLogin} />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/create-password/:token" element={<CreatePassword service={blockchainService} />} />

                <Route
                  path="/topics"
                  element={
                    <Protected role={"CentralAuthority"} roles={role}>
                      {blockchainService ? (
                        <ClaimTopicsPage service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/topics/create"
                  element={
                    <Protected role={"CentralAuthority"} roles={role}>
                      {blockchainService ? (
                        <CreateClaimTopic service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/topics/:topicId"
                  element={
                    <Protected role={"CentralAuthority"} roles={role}>
                      {blockchainService ? (
                        <ViewClaimTopic service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/topics/edit/:topicId"
                  element={
                    <Protected role={"CentralAuthority"} roles={role}>
                      {blockchainService ? (
                        <EditClaimTopic service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/issuers"
                  element={
                    <Protected role={"CentralAuthority"} roles={role}>
                      {blockchainService ? (
                        <TrustedIssuersPage service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/issuers/create"
                  element={
                    <Protected role={"CentralAuthority"} roles={role}>
                      {blockchainService ? (
                        <CreateTrustedIssuer service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/issuers/:issuerId"
                  element={
                    <Protected role={"CentralAuthority"} roles={role}>
                      {blockchainService ? (
                        <CreateTrustedIssuer service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/identities"
                  element={
                    <Protected role={"TrustedIssuer"} roles={role}>
                      {blockchainService ? (
                        <IdentitiesPage service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/identities/create"
                  element={
                    <Protected role={"TrustedIssuer"} roles={role}>
                      {blockchainService ? (
                        <CreateDigitalId service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/identities/:identityId/:userId?"
                  element={
                    <Protected role={"TrustedIssuer"} roles={role}>
                      {blockchainService ? (
                        <DigitalIdentityDetailView service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/identities/:identityId/edit"
                  element={
                    <Protected role={"TrustedIssuer"} roles={role}>
                      {blockchainService ? (
                        <EditClaims service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/identities/:identity/edit/summary"
                  element={
                    <Protected role={"TrustedIssuer"} roles={role}>
                      {blockchainService ? (
                        <EditClaimsSummaryView service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
                <Route
                  path="/mint"
                  element={
                    <Protected role={"TrustedIssuer"} roles={role}>
                      {blockchainService ? (
                        <MintPage service={blockchainService} />
                      ) : (
                        <div className="flex justify-center items-center h-full">
                          <Spin tip="Initializing service..." />
                        </div>
                      )}
                    </Protected>
                  }
                />
              </Routes>
            </div>
          </Layout>
        </Router>
      </RoleContext.Provider>
    </>
  );
}

export default App;
